import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Upload, X, AlertCircle, PartyPopper, Video } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { IMG } from "@/lib/content";

const CATEGORIES = [
  { key: "Kitchen (stove & sink)", required: true },
  { key: "Living room / common areas", required: true },
  { key: "Every bedroom", required: true },
  { key: "Every bathroom", required: true },
  { key: "Floors & baseboards", required: true },
  { key: "Pet hair / stains / grease / trash / buildup", required: false },
  { key: "Inside oven / refrigerator (if requested)", required: false },
  { key: "Any area needing special attention", required: false },
];

const inp = "mt-1 w-full rounded-xl border border-[#E6DEC3] px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#B87333]/30 bg-white";

export default function GetEstimate() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", property_type: "House",
    bedrooms: "", bathrooms: "", requested_service: "Deep Cleaning", preferred_date: "", details: "" });
  const [photos, setPhotos] = useState({}); // category -> File[]
  const [video, setVideo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const addPhotos = (cat, fileList) => {
    const chosen = Array.from(fileList);
    setPhotos((p) => ({ ...p, [cat]: [...(p[cat] || []), ...chosen] }));
  };
  const removePhoto = (cat, i) => setPhotos((p) => ({ ...p, [cat]: p[cat].filter((_, idx) => idx !== i) }));
  const totalPhotos = Object.values(photos).reduce((a, b) => a + b.length, 0);

  const submit = async (e) => {
    e.preventDefault();
    if (totalPhotos === 0) { toast.error("Please upload current property photos so we can prepare an accurate quote."); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("service_slug", "deep");
      const labels = [];
      Object.entries(photos).forEach(([cat, arr]) => arr.forEach((f) => { fd.append("files", f); labels.push(cat); }));
      fd.append("labels", labels.join("|"));
      if (video) fd.append("video", video);
      const { data } = await api.post("/estimates", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(data);
      toast.success("Quote request submitted!");
    } catch (err) { toast.error(apiErr(err)); } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center">
        <div className="w-16 h-16 rounded-full bg-[#B87333]/15 text-[#B87333] flex items-center justify-center mx-auto"><PartyPopper size={30} /></div>
        <h1 className="mt-6 font-serif text-4xl text-[#2C2621]">Quote request received</h1>
        <p className="mt-3 text-[#635B52]">We'll review your property and photos, then email you a final price and available times. You may be asked for a few more photos first.</p>
        <div className="mt-6 bg-white rounded-2xl border border-[#E6DEC3] p-6 shadow-[0_4px_20px_rgba(44,38,33,0.04)]" data-testid="estimate-confirmation">
          <p className="text-xs uppercase tracking-[0.2em] text-[#B87333] font-semibold">Quote number</p>
          <p className="font-serif text-2xl text-[#2C2621]" data-testid="estimate-number">{done.estimate_number}</p>
          <p className="mt-2 text-sm text-[#635B52]">{done.photos?.length || 0} photo(s) uploaded{done.video_path ? " + walkthrough video" : ""}</p>
        </div>
        <Link to="/" className="mt-6 inline-block bg-[#B87333] text-white rounded-full px-6 py-3 font-medium">Back to home</Link>
      </div>
    );
  }

  return (
    <div>
      <section className="relative h-[36vh] min-h-[280px]">
        <img src={IMG.deepKitchen} alt="Detailed deep clean" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-end pb-10">
          <p className="text-xs uppercase tracking-[0.25em] text-white/90">Deep cleaning · custom & unusual jobs</p>
          <h1 className="mt-2 font-serif text-4xl sm:text-5xl text-white font-semibold">Get a Quote</h1>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14">
        <div className="bg-[#B87333]/10 border border-[#B87333]/30 rounded-2xl p-5 flex gap-3 mb-8" data-testid="estimate-notice">
          <AlertCircle className="text-[#B87333] shrink-0 mt-0.5" size={20} />
          <p className="text-sm text-[#2C2621]">Your quote is based on the photos and information provided. The price may be adjusted if the property's actual condition or requested work is materially different upon arrival. An in-person inspection would only be necessary for unusually large properties, hazardous conditions, or photos that don't clearly show the scope.</p>
        </div>

        <form onSubmit={submit} className="bg-white rounded-2xl border border-[#E6DEC3] p-6 sm:p-8 shadow-[0_4px_20px_rgba(44,38,33,0.04)] space-y-4" data-testid="estimate-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="text-sm font-medium text-[#2C2621]">Full name</label><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} data-testid="est-name" /></div>
            <div><label className="text-sm font-medium text-[#2C2621]">Phone</label><input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inp} data-testid="est-phone" /></div>
          </div>
          <div><label className="text-sm font-medium text-[#2C2621]">Email</label><input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inp} data-testid="est-email" /></div>
          <div><label className="text-sm font-medium text-[#2C2621]">Service address</label><input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inp} data-testid="est-address" /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="text-sm font-medium text-[#2C2621]">Property type</label>
              <select value={form.property_type} onChange={(e) => setForm({ ...form, property_type: e.target.value })} className={inp} data-testid="est-property-type">
                {["House", "Apartment / Condo", "Townhome", "Short-term rental", "Office / Commercial", "Other"].map((x) => <option key={x}>{x}</option>)}
              </select></div>
            <div><label className="text-sm font-medium text-[#2C2621]">Requested service</label>
              <select value={form.requested_service} onChange={(e) => setForm({ ...form, requested_service: e.target.value })} className={inp} data-testid="est-service">
                {["Deep Cleaning", "Excessive Mess or Trash", "Unusual / Custom Property", "Move-in / Move-out", "Other"].map((x) => <option key={x}>{x}</option>)}
              </select></div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div><label className="text-sm font-medium text-[#2C2621]">Bedrooms</label><input type="number" min="0" required value={form.bedrooms} onChange={(e) => setForm({ ...form, bedrooms: e.target.value })} className={inp} data-testid="est-bedrooms" /></div>
            <div><label className="text-sm font-medium text-[#2C2621]">Bathrooms</label><input type="number" min="0" required value={form.bathrooms} onChange={(e) => setForm({ ...form, bathrooms: e.target.value })} className={inp} data-testid="est-bathrooms" /></div>
            <div><label className="text-sm font-medium text-[#2C2621]">Preferred date</label><input type="date" value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} className={inp} data-testid="est-preferred-date" /></div>
          </div>
          <div><label className="text-sm font-medium text-[#2C2621]">Description & special concerns</label>
            <textarea rows={3} value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} placeholder="Tell us about the condition, buildup, pets, oven/fridge, and anything needing special attention." className={inp} data-testid="est-details" /></div>

          <div className="pt-2">
            <p className="text-sm font-semibold text-[#2C2621]">Current property photos</p>
            <p className="text-xs text-[#635B52] mb-3">Please upload clear, recent photos of each area. This lets us give you an accurate quote without an in-person visit.</p>
            <div className="space-y-3">
              {CATEGORIES.map((c) => (
                <div key={c.key} className="border border-[#E6DEC3] rounded-xl p-3" data-testid={`photo-cat-${CATEGORIES.indexOf(c)}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[#2C2621]">{c.key}{c.required && <span className="text-[#B87333]"> *</span>}</span>
                    <label className="inline-flex items-center gap-1 text-[#B87333] text-sm cursor-pointer font-medium">
                      <Upload size={14} /> Add
                      <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => addPhotos(c.key, e.target.files)} data-testid={`photo-input-${CATEGORIES.indexOf(c)}`} />
                    </label>
                  </div>
                  {(photos[c.key] || []).length > 0 && (
                    <div className="mt-2 grid grid-cols-4 sm:grid-cols-6 gap-2">
                      {photos[c.key].map((f, i) => (
                        <div key={i} className="relative">
                          <img src={URL.createObjectURL(f)} alt="" className="w-full h-14 object-cover rounded border border-[#E6DEC3]" />
                          <button type="button" onClick={() => removePhoto(c.key, i)} className="absolute -top-1.5 -right-1.5 bg-[#2C2621] text-white rounded-full p-0.5"><X size={10} /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-[#2C2621] flex items-center gap-1"><Video size={15} /> Optional short walkthrough video</label>
            <input type="file" accept="video/*" onChange={(e) => setVideo(e.target.files[0] || null)} className={inp} data-testid="est-video" />
            {video && <p className="mt-1 text-xs text-[#635B52]">{video.name}</p>}
          </div>

          <button type="submit" disabled={submitting} className="w-full bg-[#B87333] hover:bg-[#A06127] text-white font-medium rounded-full px-6 py-3.5 shadow-md transition-all disabled:opacity-60" data-testid="est-submit">
            {submitting ? "Submitting…" : "Submit quote request"}
          </button>
        </form>
      </div>
    </div>
  );
}
